import { useMemo, useState, type ReactNode, type ChangeEvent } from 'react'
import { Box, Flex, Text, VStack, Textarea, ConfirmDialog, useT } from '@minetrace/ui'
import { useFormColors, FormSection } from '@minetrace/ui/forms'
import {
  LuClipboardCheck as ClipboardCheck,
  LuCheck as Check,
  LuTriangleAlert as Triangle,
  LuClock as Clock,
  LuPlus as Plus,
  LuLock as Lock,
} from 'react-icons/lu'
import { formatDateTime } from '../lib/dates'
import type { ObservationResponse, StepApprovalEntry } from '../api/models/response/proveedor'
import ObservationReplies, { type ObservationReplyItem } from './ObservationReplies'

type Intent = 'conforme' | 'observado'

interface ObservationBoxProps {
  step: number
  observations: ObservationResponse[]
  approvalEntry: StepApprovalEntry | null
  isApproving: boolean
  onAdd: (text: string) => Promise<void>
  onResolve: (obsId: string) => Promise<void>
  onApprove: () => Promise<void>
  /** Hilos de respuesta por observación (opcional; si no se pasa, no se muestran). */
  repliesByObs?: Record<string, ObservationReplyItem[]>
  /** Habilita responder en el hilo de cada observación (opcional). */
  onReply?: (obsId: string, text: string) => Promise<void>
}

const AMBER = '#d49a2a'
const AMBER_DARK = '#8a5f0b'
const AMBER_BG = '#fcf6e8'
const GREEN = '#1d6664'
const GREEN_DARK = '#1d6664'
const GREEN_BG = '#eef7f6'
const PRIMARY = '#1d6664'
const PRIMARY_HOVER = '#085a30'
const NEUTRAL_BORDER = '#e8dfd1'
const STALE_BG = '#f9eccf'
const STALE_BORDER = '#d49a2a'
const STALE_TEXT = '#92400e'

export default function ObservationBox({
  step,
  observations,
  approvalEntry,
  isApproving,
  onAdd,
  onResolve,
  onApprove,
  repliesByObs,
  onReply,
}: ObservationBoxProps) {
  const c = useFormColors()
  const { t } = useT()

  const stepObs = useMemo(() => observations.filter(o => o.step === step), [observations, step])
  const pendingCount = stepObs.filter(o => !o.resolvedAt).length
  const resolvedCount = stepObs.length - pendingCount

  const status = approvalEntry?.status ?? 'pending'
  const isApproved = status === 'approved'
  const isStale = status === 'stale'
  const isBlocked = pendingCount > 0

  // Initial intent: approved → conforme; has obs → observado; else conforme by default.
  const [intent, setIntent] = useState<Intent>(
    isApproved ? 'conforme' : pendingCount > 0 ? 'observado' : 'conforme',
  )
  const [newText, setNewText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resolveTarget, setResolveTarget] = useState<ObservationResponse | null>(null)
  const [isResolving, setIsResolving] = useState(false)
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false)

  const handleAdd = async () => {
    if (!newText.trim() || isSubmitting) return
    setIsSubmitting(true)
    try {
      await onAdd(newText.trim())
      setNewText('')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResolveConfirm = async () => {
    if (!resolveTarget) return
    setIsResolving(true)
    try {
      await onResolve(resolveTarget.id)
      setResolveTarget(null)
    } finally {
      setIsResolving(false)
    }
  }

  const handleApproveConfirm = async () => {
    await onApprove()
    setConfirmApproveOpen(false)
  }

  return (
    <FormSection
      icon={ClipboardCheck}
      title={t('shared.observations.diagnosticTitle', { defaultValue: 'Diagnóstico' })}
      description={t('shared.observations.diagnosticDescription', { defaultValue: 'Marca el resultado de tu evaluación.' })}
    >
      {/* Stale warning */}
      {isStale && (
        <Flex
          alignItems="flex-start"
          gap={2}
          p={3}
          mb={4}
          bg={STALE_BG}
          borderLeft="3px solid"
          borderLeftColor={STALE_BORDER}
          borderRadius="md"
        >
          <Box mt={0.5} flexShrink={0} color={STALE_BORDER}>
            <Triangle size={16} />
          </Box>
          <Box flex={1}>
            <Text fontSize="sm" fontWeight="600" color={STALE_TEXT}>
              {t('proveedores.wizard.evaluar.stepApproval.staleWarning', {
                defaultValue: 'Aprobación obsoleta — la información cambió, re-aprobá si es correcta.',
              })}
            </Text>
            {approvalEntry?.approvedAt && (
              <Text fontSize="xs" color={c.textMuted} mt={1}>
                {t('proveedores.wizard.evaluar.stepApproval.stalePreviouslyApproved', {
                  defaultValue: 'Aprobada el {{date}} (datos modificados)',
                  date: formatDateTime(approvalEntry.approvedAt),
                })}
              </Text>
            )}
          </Box>
        </Flex>
      )}

      {/* Segmented intent toggle */}
      <SegmentedToggle
        intent={intent}
        onChange={setIntent}
        pendingCount={pendingCount}
        isApprovedLocked={isApproved}
      />

      {/* Conforme view */}
      {intent === 'conforme' && (
        <Box mt={4}>
          {isApproved ? (
            <ApprovedBanner entry={approvalEntry!} />
          ) : (
            <ApproveCTA
              isBlocked={isBlocked}
              pendingCount={pendingCount}
              isApproving={isApproving}
              onClick={() => setConfirmApproveOpen(true)}
              isStale={isStale}
            />
          )}
        </Box>
      )}

      {/* Observado view */}
      {intent === 'observado' && !isApproved && (
        <Box mt={4}>
          <Text fontSize="xs" fontWeight="700" color={AMBER_DARK} textTransform="uppercase" letterSpacing="wider" mb={2}>
            {t('shared.observations.detailLabel', { defaultValue: 'Nueva observación' })}
          </Text>
          <Textarea
            value={newText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNewText(e.target.value)}
            placeholder={t('shared.observations.placeholder', { defaultValue: 'Especificá qué debe corregir el proveedor...' })}
            rows={3}
            bg={c.bgInput}
            border="1px solid"
            borderColor={NEUTRAL_BORDER}
            borderRadius="lg"
            color={c.text}
            _placeholder={{ color: c.textMuted }}
            _focus={{ outline: 'none', borderColor: AMBER, boxShadow: `0 0 0 1px ${AMBER}` }}
          />
          <Flex mt={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Text fontSize="xs" color={c.textMuted}>
              {t('shared.observations.providerNoticeShort', { defaultValue: 'Será enviada al proveedor exactamente así.' })}
            </Text>
            <Box
              as="button"
              type="button"
              display="inline-flex"
              alignItems="center"
              gap={2}
              px={4}
              h="36px"
              bg={!newText.trim() || isSubmitting ? '#b5aa9d' : PRIMARY}
              color="white"
              borderRadius="lg"
              fontSize="sm"
              fontWeight="600"
              cursor={!newText.trim() || isSubmitting ? 'not-allowed' : 'pointer'}
              _hover={!newText.trim() || isSubmitting ? {} : { bg: PRIMARY_HOVER }}
              onClick={handleAdd}
              disabled={!newText.trim() || isSubmitting}
            >
              <Plus size={14} />
              {t('shared.observations.addButton', { defaultValue: 'Agregar observación' })}
            </Box>
          </Flex>
        </Box>
      )}

      {/* Existing observations list — always visible */}
      {stepObs.length > 0 && (
        <Box mt={6}>
          <Flex alignItems="center" gap={2} mb={3}>
            <Text fontSize="xs" fontWeight="700" color={c.textMuted} textTransform="uppercase" letterSpacing="wider">
              {t('shared.observations.listLabel', { defaultValue: 'Observaciones del paso' })}
            </Text>
            {pendingCount > 0 && (
              <Box px={2} py={0.5} bg={AMBER_BG} color={AMBER_DARK} borderRadius="full" fontSize="11px" fontWeight="700">
                {t('shared.observations.pendingPill', { defaultValue: '{{count}} pendientes', count: pendingCount })}
              </Box>
            )}
            {resolvedCount > 0 && (
              <Box px={2} py={0.5} bg={GREEN_BG} color={GREEN_DARK} borderRadius="full" fontSize="11px" fontWeight="700">
                {t('shared.observations.resolvedPill', { defaultValue: '{{count}} resueltas', count: resolvedCount })}
              </Box>
            )}
          </Flex>
          <VStack gap={2} alignItems="stretch">
            {stepObs.map(obs => (
              <ObservationCard
                key={obs.id}
                obs={obs}
                isApprovedStep={isApproved}
                onResolveClick={() => setResolveTarget(obs)}
                isResolving={isResolving && resolveTarget?.id === obs.id}
                replies={repliesByObs?.[obs.id] ?? []}
                onReply={onReply}
              />
            ))}
          </VStack>
        </Box>
      )}

      {/* Resolve confirmation modal */}
      <ConfirmDialog
        open={resolveTarget !== null}
        onClose={() => { if (!isResolving) setResolveTarget(null) }}
        onConfirm={handleResolveConfirm}
        title={t('shared.observations.confirmResolveTitle', { defaultValue: '¿Marcar observación como resuelta?' })}
        confirmLabel={t('shared.observations.confirmResolveConfirm', { defaultValue: 'Sí, resolver' })}
        cancelLabel={t('shared.observations.confirmResolveCancel', { defaultValue: 'Cancelar' })}
        variant="default"
        isLoading={isResolving}
      >
        {resolveTarget && (
          <Box>
            <Text fontSize="sm" color="#475569" mb={3}>
              {t('shared.observations.confirmResolveBody', {
                defaultValue: 'Esto la marcará como subsanada. La observación seguirá visible en el historial. El paso seguirá pendiente hasta que lo marqués como Conforme.',
              })}
            </Text>
            <Box p={3} bg="#f8fafc" borderLeft="3px solid" borderLeftColor={AMBER} borderRadius="md">
              <Text fontSize="sm" color="#334155" fontStyle="italic">
                &ldquo;{resolveTarget.observationText}&rdquo;
              </Text>
              <Text fontSize="xs" color="#94a3b8" mt={1}>
                {resolveTarget.createdByUserName ?? t('shared.observations.defaultUser', { defaultValue: 'Usuario' })}
                {' · '}
                {formatDateTime(resolveTarget.createdAt)}
              </Text>
            </Box>
          </Box>
        )}
      </ConfirmDialog>

      {/* Approve step confirmation modal */}
      <ConfirmDialog
        open={confirmApproveOpen}
        onClose={() => { if (!isApproving) setConfirmApproveOpen(false) }}
        onConfirm={handleApproveConfirm}
        title={t('proveedores.wizard.evaluar.confirmApproveStepModal.title', { defaultValue: '¿Aprobar este paso?' })}
        confirmLabel={t('proveedores.wizard.evaluar.confirmApproveStepModal.confirm', { defaultValue: 'Sí, aprobar' })}
        cancelLabel={t('proveedores.wizard.evaluar.confirmApproveStepModal.cancel', { defaultValue: 'Cancelar' })}
        variant="default"
        isLoading={isApproving}
      >
        {t('proveedores.wizard.evaluar.confirmApproveStepModal.body', {
          defaultValue: 'Vas a marcar este paso como aprobado. Si más adelante el proveedor cambia esta sección, la aprobación quedará obsoleta y vas a tener que re-aprobar.',
        })}
      </ConfirmDialog>
    </FormSection>
  )
}

// ────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────

function SegmentedToggle({
  intent,
  onChange,
  pendingCount,
  isApprovedLocked,
}: {
  intent: Intent
  onChange: (v: Intent) => void
  pendingCount: number
  isApprovedLocked: boolean
}) {
  const { t } = useT()
  const isObservado = intent === 'observado'

  return (
    <Flex
      bg="#f1f5f9"
      p={1}
      borderRadius="xl"
      gap={1}
      maxW="480px"
      role="tablist"
    >
      <SegmentButton
        active={!isObservado}
        onClick={() => onChange('conforme')}
        activeBg="#ffffff"
        activeColor={GREEN_DARK}
        accentColor={GREEN}
        icon={<Check size={16} />}
        label={t('shared.observations.intentConforme', { defaultValue: 'Conforme' })}
        disabled={false}
      />
      <SegmentButton
        active={isObservado}
        onClick={() => onChange('observado')}
        activeBg="#ffffff"
        activeColor={AMBER_DARK}
        accentColor={AMBER}
        icon={<Triangle size={16} />}
        label={t('shared.observations.intentObservado', { defaultValue: 'Observado' })}
        badge={pendingCount > 0 ? pendingCount : undefined}
        disabled={isApprovedLocked}
      />
    </Flex>
  )
}

interface SegmentButtonProps {
  active: boolean
  onClick: () => void
  activeBg: string
  activeColor: string
  accentColor: string
  icon: ReactNode
  label: string
  badge?: number
  disabled?: boolean
}

function SegmentButton({ active, onClick, activeBg, activeColor, accentColor, icon, label, badge, disabled }: SegmentButtonProps) {
  return (
    <Box
      as="button"
      type="button"
      role="tab"
      aria-selected={active}
      aria-disabled={disabled}
      flex={1}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={2}
      h="40px"
      borderRadius="lg"
      fontSize="sm"
      fontWeight="700"
      bg={active ? activeBg : 'transparent'}
      color={active ? activeColor : disabled ? '#94a3b8' : '#64748b'}
      boxShadow={active ? '0 1px 2px rgba(15,23,42,0.08)' : 'none'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      opacity={disabled ? 0.6 : 1}
      transition="all 120ms ease"
      onClick={disabled ? undefined : onClick}
      _hover={!active && !disabled ? { color: activeColor } : undefined}
    >
      <Box color={active ? accentColor : 'inherit'} display="inline-flex">
        {icon}
      </Box>
      {label}
      {badge !== undefined && badge > 0 && (
        <Box
          minW="18px"
          h="18px"
          px={1.5}
          bg={active ? accentColor : '#cbd5e1'}
          color={active ? '#ffffff' : '#475569'}
          borderRadius="full"
          fontSize="11px"
          fontWeight="700"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          {badge}
        </Box>
      )}
    </Box>
  )
}

function ApprovedBanner({ entry }: { entry: StepApprovalEntry }) {
  const { t } = useT()
  const userLabel = entry.approvedByUsername ?? entry.approvedByUserId ?? '—'
  const dateLabel = formatDateTime(entry.approvedAt)
  return (
    <Flex
      alignItems="center"
      gap={3}
      p={4}
      bg={GREEN_BG}
      borderRadius="lg"
      border="1px solid"
      borderColor="#bfe3df"
    >
      <Flex
        w="40px" h="40px"
        borderRadius="full"
        bg={GREEN}
        color="#ffffff"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Check size={22} />
      </Flex>
      <Box flex={1}>
        <Text fontSize="sm" fontWeight="700" color={GREEN_DARK}>
          {t('proveedores.wizard.evaluar.stepApproval.approvedTitle', { defaultValue: 'Paso aprobado' })}
        </Text>
        <Text fontSize="xs" color={GREEN_DARK} mt={0.5}>
          {t('proveedores.wizard.evaluar.stepApproval.approvedBy', {
            defaultValue: 'Aprobado por {{username}} el {{date}}',
            username: userLabel,
            date: dateLabel,
          })}
        </Text>
      </Box>
    </Flex>
  )
}

function ApproveCTA({
  isBlocked,
  pendingCount,
  isApproving,
  onClick,
  isStale,
}: {
  isBlocked: boolean
  pendingCount: number
  isApproving: boolean
  onClick: () => void
  isStale: boolean
}) {
  const { t } = useT()
  const disabled = isBlocked || isApproving

  return (
    <Box>
      <Box
        as="button"
        type="button"
        w="100%"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        gap={2}
        h="48px"
        bg={disabled ? '#b5aa9d' : PRIMARY}
        color="#ffffff"
        borderRadius="lg"
        fontSize="sm"
        fontWeight="700"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        _hover={disabled ? {} : { bg: PRIMARY_HOVER }}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        title={isBlocked
          ? t('proveedores.wizard.evaluar.stepApproval.blockedTooltip', {
              defaultValue: 'Resolvé las observaciones para aprobar este paso',
            })
          : undefined}
      >
        {isBlocked ? <Lock size={16} /> : <Check size={16} />}
        {isApproving
          ? t('proveedores.wizard.evaluar.stepApproval.approving', { defaultValue: 'Aprobando...' })
          : isStale
            ? t('proveedores.wizard.evaluar.stepApproval.reapproveButton', { defaultValue: 'Re-aprobar este paso' })
            : t('proveedores.wizard.evaluar.stepApproval.approveButton', { defaultValue: 'Aprobar este paso' })}
      </Box>
      {isBlocked && (
        <Flex mt={2} gap={1.5} alignItems="center" justifyContent="center" color={AMBER_DARK} fontSize="xs">
          <Triangle size={12} />
          <Text fontSize="xs" color={AMBER_DARK}>
            {t('shared.observations.approveBlockedNote', {
              defaultValue: 'Hay {{count}} observación(es) sin resolver. Resolvelas o eliminalas antes de aprobar.',
              count: pendingCount,
            })}
          </Text>
        </Flex>
      )}
    </Box>
  )
}

function ObservationCard({
  obs,
  isApprovedStep,
  onResolveClick,
  isResolving,
  replies,
  onReply,
}: {
  obs: ObservationResponse
  isApprovedStep: boolean
  onResolveClick: () => void
  isResolving: boolean
  replies: ObservationReplyItem[]
  onReply?: (obsId: string, text: string) => Promise<void>
}) {
  const { t } = useT()
  const isResolved = !!obs.resolvedAt
  const accent = isResolved ? GREEN : AMBER
  const accentDark = isResolved ? GREEN_DARK : AMBER_DARK
  const tintBg = isResolved ? GREEN_BG : AMBER_BG
  // Once the step is approved, no actions on observations.
  const canResolve = !isResolved && !isApprovedStep

  return (
    <Flex
      p={3}
      gap={3}
      alignItems="flex-start"
      bg={tintBg}
      borderRadius="lg"
      borderLeft="3px solid"
      borderLeftColor={accent}
    >
      <Box mt={0.5} flexShrink={0} color={accentDark}>
        {isResolved ? <Check size={16} /> : <Clock size={16} />}
      </Box>

      <Box flex={1} minW={0}>
        <Text fontSize="sm" color="#2f2a26" lineHeight="snug">
          {obs.observationText}
        </Text>
        <Flex mt={1.5} gap={2} alignItems="center" flexWrap="wrap" fontSize="xs" color="#64748b">
          <Text>{obs.createdByUserName ?? t('shared.observations.defaultUser', { defaultValue: 'Usuario' })}</Text>
          <Text color="#cbd5e1">·</Text>
          <Text>{formatDateTime(obs.createdAt)}</Text>
          {isResolved && obs.resolvedAt && (
            <>
              <Text color="#cbd5e1">·</Text>
              <Flex alignItems="center" gap={1} color={GREEN_DARK} fontWeight="600">
                <Check size={11} />
                <Text fontSize="xs" color={GREEN_DARK}>
                  {t('shared.observations.resolvedShort', { defaultValue: 'resuelta' })} {formatDateTime(obs.resolvedAt)}
                </Text>
              </Flex>
            </>
          )}
        </Flex>
        {(replies.length > 0 || onReply) && (
          <ObservationReplies replies={replies} canReply={Boolean(onReply)} onReply={(text) => onReply!(obs.id, text)} />
        )}
      </Box>

      {canResolve && (
        <Box
          as="button"
          type="button"
          flexShrink={0}
          px={3}
          h="30px"
          borderRadius="md"
          fontSize="xs"
          fontWeight="700"
          bg="#ffffff"
          color={accentDark}
          border="1px solid"
          borderColor={accent}
          cursor={isResolving ? 'not-allowed' : 'pointer'}
          opacity={isResolving ? 0.6 : 1}
          _hover={isResolving ? {} : { bg: accent, color: '#ffffff' }}
          onClick={isResolving ? undefined : onResolveClick}
          disabled={isResolving}
        >
          {t('shared.observations.resolveButton', { defaultValue: 'Resolver' })}
        </Box>
      )}
    </Flex>
  )
}
